import { TimeTracksAll } from "./user/workspace/TimeTracksAll";
import { DateRangeField } from "../components/daterange/DateRangeField";
import { TodoCreateButton } from "../components/TodoCreateButton";
import { transformFilters } from "./todo/model/TodoConnectionFilter";
import { defaultFilters } from "./todo/components/TodoListDefaultFilters";
import { Icon } from "../components/Icon";
import { ButtonPlayground } from "./dev/ButtonPlayground";
import { Navigate, useParams } from "react-router";
import type { EntryPointRouteObject } from "@loop-payments/react-router-relay";
import { useViewerUsername } from "../app-controller/ViewerContext";
import { HeaderLoader } from "../components/HeaderLoading";
import { NotFoundRoute } from "./NotFoundRoute";
import { OAuthAuthorizeRoute } from "./oauth/OAuthAuthorizeRoute";
import { CallPlayground } from "./dev/CallPlayground";
import DashboardEntryPoint from "./dashboard/Dashboard.entrypoint";
import UsersEntryPoint from "./user/Users.entrypoint";
import TimeTracksEntryPoint from "./user/workspace/TimeTracks.entrypoint";
import UserEntryPoint from "./user/User.entrypoint";
import UserPermissionsEntryPoint from "./user/management/UserPermissions.entrypoint";
import UserManagementEntryPoint from "./user/management/UserManagement.entrypoint";
import CustomersRouteEntryPoint from "./customer/CustomersRoute.entrypoint";
import CustomerEntryPoint from "./customer/Customer.entrypoint";
import CustomerEditRouteEntryPoint from "./customer/edit/CustomerEditRoute.entrypoint";
import CustomerBillingsEntryPoint from "./customer/billing/CustomerBillings.entrypoint";
import CustomerBillingEntryPoint from "./billing/CustomerBilling.entrypoint";
import CustomerBillingsMonthEntryPoint from "./billings/CustomerBillingsMonth.entrypoint";
import TodosEntryPoint from "./todo/overview/Todos.entrypoint";
import TodosDoneEntryPoint from "./todo/overview/TodosDone.entrypoint";
import TimeTrackRouteEntryPoint from "./timetrack/TimeTrackRoute.entrypoint";
import CustomerCategoriesEntryPoint from "./organization/customer-categories/CustomerCategories.entrypoint";
import ItemsOverviewEntryPoint from "./item/ItemsOverview.entrypoint";
import CreateTokenEntryPoint from "./tools/CreateToken.entrypoint";
import DailyQuarterHourlyHistogramEntryPoint from "./insights/DailyQuarterHourlyHistogram.entrypoint";
import CallsRouteEntryPoint from "./call/Calls.entrypoint";

function UserRedirect() {
  const username = useViewerUsername();
  if (!username) {
    return <HeaderLoader />;
  }
  return <Navigate to={`/user/${username}`} replace />;
}

function UserWorkspaceRedirect() {
  const username = useViewerUsername();
  if (!username) {
    return <HeaderLoader />;
  }
  return <Navigate to={`/user/${username}/workspace`} replace />;
}

function CustomerBillingRedirect({
  customerBillingId,
}: {
  customerBillingId: string;
}) {
  return <Navigate to={`/billing/${customerBillingId}`} replace />;
}

function CustomerBillingRedirectRoute() {
  const { customerBillingId } = useParams<{
    customerBillingId: string;
  }>();
  return <CustomerBillingRedirect customerBillingId={customerBillingId!} />;
}

export const routes: EntryPointRouteObject[] = [
  { path: "/", entryPoint: DashboardEntryPoint },

  { path: "/oauth/authorize", element: <OAuthAuthorizeRoute /> },

  { path: "/user", entryPoint: UsersEntryPoint },

  {
    // Currently not implemented in web app. Deeplink from iOS app.
    path: "/review",
    // "all" will redirect to "me" if user doesn't have necessary rights.
    element: <Navigate to="/user/all/workspace" replace />,
  },

  {
    path: "/user/me",
    element: <UserRedirect />,
  },
  {
    path: "/user/me/workspace",
    element: <UserWorkspaceRedirect />,
  },
  { path: "/user/all/workspace", element: <TimeTracksAll /> },
  { path: "/user/:id/workspace", entryPoint: TimeTracksEntryPoint },
  { path: "/user/management", entryPoint: UserManagementEntryPoint },
  { path: "/user/permissions", entryPoint: UserPermissionsEntryPoint },
  { path: "/user/:id", entryPoint: UserEntryPoint },

  { path: "/customer", entryPoint: CustomersRouteEntryPoint },
  { path: "/customer/:id", entryPoint: CustomerEntryPoint },
  { path: "/customer/:id/edit", entryPoint: CustomerEditRouteEntryPoint },
  {
    path: "/customer/:customerId/billing",
    entryPoint: CustomerBillingsEntryPoint,
  },
  {
    path: "/customer/:customerId/billing/:customerBillingId",
    element: <CustomerBillingRedirectRoute />,
  },

  {
    path: "/billing/:customerBillingId",
    entryPoint: CustomerBillingEntryPoint,
  },

  {
    path: "/billings/:month",
    entryPoint: CustomerBillingsMonthEntryPoint,
  },

  { path: "/todo", entryPoint: TodosEntryPoint },
  { path: "/todo/done", entryPoint: TodosDoneEntryPoint },

  { path: "/timetrack/:id", entryPoint: TimeTrackRouteEntryPoint },

  {
    path: "/organization/customer-categories",
    entryPoint: CustomerCategoriesEntryPoint,
  },

  { path: "/item", entryPoint: ItemsOverviewEntryPoint },

  { path: "/tools/token", entryPoint: CreateTokenEntryPoint },

  {
    path: "/insights/histogram",
    entryPoint: DailyQuarterHourlyHistogramEntryPoint,
  },

  { path: "/calls", entryPoint: CallsRouteEntryPoint },

  ...(process.env.NODE_ENV !== "production"
    ? [
        {
          path: "/dev/date-range",
          element: <DevDateRangeField />,
        },
        {
          path: "/dev/todo-create",
          element: <DevTodoCreateButton />,
        },
        {
          path: "/dev/button",
          element: <DevButtonPlayground />,
        },
        {
          path: "/dev/calls",
          element: <CallPlayground />,
        },
      ]
    : []),

  {
    path: "*",
    element: <NotFoundRoute />,
  },
];

function DevButtonPlayground() {
  return <ButtonPlayground />;
}

function DevDateRangeField() {
  return (
    <DateRangeField
      startDate={null}
      endDate={null}
      onChange={console.log.bind(console, "onChange")}
      onAbort={console.log.bind(console, "onAbort")}
    />
  );
}

function DevTodoCreateButton() {
  return (
    <TodoCreateButton
      filters={transformFilters(defaultFilters)}
      icon={<Icon icon="plus" type="regular" />}
    >
      Todo erstellen
    </TodoCreateButton>
  );
}
